import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { tokens } from "../../../theme";
import { useRestaurant } from "../../../hooks/RestaurantContext";
import Loading from "../../../components/loading";
import Error from "../../../components/Error";
import NoDataTable from "../../../components/noDataTable";
import TableActions from "../../../components/tableActions";
import ParagraphHighliter from "../../../components/paragraphHighliter";
import { ellipsisText } from "../../../global/functions/textFormat";
import { useTranslation } from "react-i18next";

const ListViewProducts = ({
  products,
  page,
  limit,
  handleClickOpen,
  handleModify,
  handleDiscount,
  handleViewDetails,
  status,
  error,
  search,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { currency } = useRestaurant();

  return (
    <TableContainer>
      <Table sx={{ backgroundColor: colors.primary[400] }}>
        <TableHead sx={{ backgroundColor: colors.primary[700] }}>
          <TableRow>
            <TableCell sx={{ width: 60 }}>{t("table.number")}</TableCell>
            <TableCell sx={{ width: 90 }}>{t("table.image")}</TableCell>
            <TableCell sx={{ width: "18%", whiteSpace: "nowrap" }}>
              {t("table.name")}
            </TableCell>
            <TableCell sx={{ width: "30%" }}>
              {t("table.description")}
            </TableCell>
            <TableCell align="center" sx={{ width: "12%", whiteSpace: "nowrap" }}>
              {t("table.price")}
            </TableCell>
            <TableCell sx={{ width: "16%", whiteSpace: "nowrap" }}>
              {t("table.options")}
            </TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }}>
              {t("table.visibility")}
            </TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }}>
              {t("table.availability")}
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
              {t("table.actions")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {status === "loadingFetchDataPaginated" ? (
            <TableRow>
              <TableCell colSpan={9} align="center">
                <Loading size={8} />
              </TableCell>
            </TableRow>
          ) : status === "fetchErrorPaginated" ? (
            <TableRow>
              <TableCell colSpan={9} align="center">
                <Error>{error}</Error>
              </TableCell>
            </TableRow>
          ) : products && products.length > 0 ? (
            products.map((item, index) => {
              const hasDiscount = item.discountValue && item.discountValue > 0;
              return (
                <TableRow key={item._id} hover>
                  <TableCell>{page * limit + index + 1}</TableCell>
                  <TableCell>
                    <img
                      src={`${process.env.REACT_APP_MEDIA_URL}/${item.image}`}
                      alt={item.name}
                      style={{
                        width: "48px",
                        height: "48px",
                        objectFit: "contain",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <ParagraphHighliter title={item?.name} search={search} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <ParagraphHighliter
                      title={ellipsisText(item?.description, 60)}
                      search={search}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {hasDiscount ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 0.25,
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            textDecoration: "line-through",
                            color: "lightcoral",
                            fontWeight: "bold",
                            fontSize: "0.75rem",
                          }}
                        >
                          {item.originalPrice ?? item.price} {currency}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{ color: "mediumseagreen", fontWeight: "bold" }}
                        >
                          {item.discountedPrice
                            ? item.discountedPrice.toFixed(2)
                            : (
                                (item.originalPrice ?? item.price) -
                                item.discountValue
                              ).toFixed(2)}{" "}
                          {currency}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography
                        component="span"
                        sx={{ fontWeight: "bold", display: "block", textAlign: "center" }}
                      >
                        {item.price} {currency}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {item.type?.[0]?.name ?? "—"}
                    {item.type?.length > 1 && (
                      <Tooltip
                        title={
                          <div>
                            {item.type.slice(1).map((type) => (
                              <div key={type._id}>{type.name}</div>
                            ))}
                          </div>
                        }
                        arrow
                      >
                        <Chip
                          size="small"
                          label={`+${item.type.length - 1}`}
                          color="default"
                          sx={{ ml: 0.5, borderRadius: "50%" }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Chip
                      variant="outlined"
                      label={item.visible ? "Visible" : "Caché"}
                      color={item.visible ? "success" : "error"}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Chip
                      variant="outlined"
                      label={item.outOfStock ? "Rupture de stock" : "En stock"}
                      color={item.outOfStock ? "error" : "success"}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <TableActions
                      actions={[
                        {
                          type: "view",
                          color: "info",
                          label: t("common.viewDetails"),
                          onClick: () => handleViewDetails(item),
                        },
                        {
                          type: "delete",
                          color: "error",
                          label: t("common.delete"),
                          onClick: () => handleClickOpen(item._id),
                        },
                        {
                          type: "edit",
                          color: "warning",
                          label: t("common.edit"),
                          onClick: () => handleModify(item),
                        },
                        {
                          type: "discount",
                          color: hasDiscount ? "success" : "info",
                          label: t("common.discount"),
                          onClick: () => handleDiscount(item),
                        },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <NoDataTable cols={9} />
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ListViewProducts;
